import clsx from "clsx";
import { LuPlus } from "react-icons/lu";
import { LoadingCircle } from "../common";
import { PiMapTrifold } from "react-icons/pi";
import { VscSettings } from "react-icons/vsc";
import { Dropdown } from "../common/dropdown";
import { CityIcon } from "../../assets/icons";
import bedIcon from "../../assets/bedIcon.svg";
import { useNavigate } from "react-router-dom";
import { BiChevronDown } from "react-icons/bi";
import { debounce } from "../../utils/debounce";
import { FaLocationArrow } from "react-icons/fa";
import bathIcon from "../../assets/bathIcon.svg";
import SqftIcon from "../../assets/sqftIcon.svg";
import { formatPrice } from "../../utils/formatPrice";
import { formatDate } from "../../utils/dateFormatter";
import { IoCloseCircleOutline } from "react-icons/io5";
import { IoIosList, IoIosSearch } from "react-icons/io";
import { Range, getTrackBackground } from "react-range";
import { useMapStore } from "../../stores/mapCordinations";
import { DropdownMultiple } from "../common/dropdownMultiple";
import { MultiSelectGroup } from "../common/multiSelectGroup";
import { useFilterPanelStore } from "../../stores/filterStore";
import { FilterPanel } from "../common/FilterPanel/filterPanel";
import { useMapFiltersStore } from "../../stores/filterMapStore";
import GrayArrowDownIcon from "../../assets/grayArrowDownIcon.svg";
import ListingSearchIcon from "../../assets/listingSearchIcon.svg";
import { selectedCitiesCoordinates } from "../../citiesWithLatLong";
import { convertToFormattedAmount } from "../../utils/convertAmount";
import { propertyPreferences } from "../../mocks/propertyPreferencesData";
import { propertyTypeLabels } from "../common/FilterPanel/propertyTypeSelector";
import { useSearchStore } from "../../stores/newStores/exploreListingSearchStore";
import {
  ListingCard,
  fetchExploreListings,
} from "../../services/exploreListings";
import {
  propertyActiveFilter,
  propertyDeListedAndSoldFilter,
} from "../../mocks/newMockData/filterPanelData";
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { set } from "lodash";
import { useShowLoginModalStore } from "../../stores/loginModalStore";
import { useAuth } from "../../context/authProvider";

interface PropertyDetailsProps {
  icon: string;
  label: string;
  value: string;
}

const PropertyDetails = ({ icon, label, value }: PropertyDetailsProps) => (
  <div className="flex items-center gap-1 text-[#7D7D7D]">
    <img src={icon} alt={label} className="" />
    <div className="text-sm">{value}</div>
  </div>
);

const MIN = 0;
const MAX_PRICE = 5000000;

interface SearchFieldProps {
  switchMode: "list" | "map";
  setSwitchMode: (switchMode: "list" | "map") => void;
}

const SearchField = ({ switchMode, setSwitchMode }: SearchFieldProps) => {
  const { filterPanelVisible } = useFilterPanelStore();
  const [properties, setProperties] = useState<ListingCard[]>([]);
  const { showLoginModal, setShowLoginModal } = useShowLoginModalStore();
  const { authenticated } = useAuth();

  const [openBedAndBath, setOpenBedAndBath] = useState(false);
  const searchResultRef = useRef<HTMLDivElement | null>(null);
  const [openPriceBox, setOpenPriceBox] = useState(false);
  const {
    propertyPrice,
    setPropertyPrice,
    setBeds,
    setBaths,
    beds,
    baths,
    setOpenFilterPanel,
    openFilterPanel,
    selectedOptions,
    setSelectedOption,
    togglesPropertyType,
    togglePropertyTypeButton,
    isDropdownOpen,
    setIsDropdownOpen,
  } = useMapFiltersStore();

  useEffect(() => {
    if (openPriceBox || openBedAndBath) {
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
  }, [openPriceBox, openBedAndBath, setIsDropdownOpen]);

  const {
    searchValue,
    setSearchValue,
    searchResult,
    setSearchResult,
    filteredResults,
    setFilteredResults,
    isLoading,
    setLoading,
  } = useSearchStore();
  const {
    coordinatesLatLng,
    setCoordinatesLatLng,
    setShowProperty,
    setPassListingId,
  } = useMapStore();

  const fetchExploreListingSearch = async (query: string) => {
    const filteredCities = Object.entries(selectedCitiesCoordinates)
      .filter(([cityName]) =>
        cityName.toLowerCase().includes(query.toLowerCase())
      )
      .map(([cityName, coordinates]) => ({
        city: cityName,
        coordinates,
      }));

    setFilteredResults({ city: filteredCities });
  };

  const handleSearch = useCallback(
    debounce((value: string) => {
      if (value.length > 0) {
        setSearchResult(true);
        fetchExploreListingSearch(value);
      } else {
        setSearchResult(false);
        setFilteredResults({ city: [] });
      }
    }, 300),
    []
  );

  const fetchProperties = useCallback(
    debounce(async (search: string) => {
      if (!search.trim()) {
        setProperties([]);
        return;
      }
      const formattedSearch = encodeURIComponent(search.trim());

      setLoading(true);

      try {
        const fetchedProperties = await fetchExploreListings(
          "1",
          "100",
          "search",
          undefined,
          undefined,
          undefined,
          undefined,
          search.trim(),
          undefined
        );

        setProperties(fetchedProperties);
      } catch (error) {
        console.error("Error fetching properties:", error);
      } finally {
        setLoading(false);
      }
    }, 500),
    []
  );

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setSearchValue(value);
    fetchProperties(value);
    handleSearch(value);
  };

  const handleCityClick = (coordinates: []) => {
    setSearchResult(false);
    setCoordinatesLatLng(coordinates);
    setSearchValue("");
  };

  const MAPBOX_TOKEN =
    "pk.eyJ1IjoiYmVhcmNvZGVzODcwIiwiYSI6ImNtMHBmMjVqZzAwZWkycXEwYmcxcHJjMGwifQ.UeHMFKKaRuaTR-NqOPPsnw";

  const getLocationFromCoords = async (latitude: number, longitude: number) => {
    const accessToken = MAPBOX_TOKEN;
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${accessToken}`;

    const response = await fetch(url);
    const data = await response.json();

    if (data.features && data.features.length > 0) {
      const location = data.features[0].place_name;
      return location;
    } else {
      return "Unknown location";
    }
  };
  const [isExpanded, setExpanded] = useState(false);

  const handleCurrentLocationClick = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          const location = await getLocationFromCoords(latitude, longitude);
          setSearchValue(location);
          setSearchResult(false);
        },
        (error) => {
          console.error("Error getting location", error);
          alert("Unable to retrieve your location.");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;

    return text;

    // const escapedQuery = query.replace(/[.*+?^=!:${}()|\[\]\/\\]/g, "\\$&");
    // const regex = new RegExp(`(${escapedQuery})`, "gi");

    // // Split the text into parts based on the query matches
    // const parts = text.split(regex);

    // return (
    // 	<>
    // 		{parts.map((part, index) => (
    // 			<span key={index}>
    // 				{regex.test(part) ? ( // Check if the part matches the query
    // 					<span className="font-bold">{part}</span>
    // 				) : (
    // 					part
    // 				)}
    // 			</span>
    // 		))}
    // 	</>
    // );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchResultRef.current &&
        !searchResultRef.current.contains(event.target as Node)
      ) {
        setSearchResult(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const bedAndBathBoxRef = useRef<HTMLDivElement>(null);
  const priceBoxRef = useRef<HTMLDivElement>(null);
  const filterBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isOutsideBedAndBath =
        bedAndBathBoxRef.current &&
        !bedAndBathBoxRef.current.contains(event.target as Node);
      const isOutsidePriceBox =
        priceBoxRef.current &&
        !priceBoxRef.current.contains(event.target as Node);
      const isOutsideFilterBox =
        filterBoxRef.current &&
        !filterBoxRef.current.contains(event.target as Node);

      if (isOutsidePriceBox) {
        setOpenPriceBox(false);
      }
      if (isOutsideBedAndBath) {
        setOpenBedAndBath(false);
      }
      if (isOutsideFilterBox) {
        setOpenFilterPanel(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openBedAndBath, openPriceBox, openFilterPanel]);

  const navigate = useNavigate();

  const handleListingClick = (id: string) => {
    setShowProperty((prev) => !prev);
    setPassListingId(id);
  };

  return (
    <div className="flex justify-between items-center h-full w-full bg-white pl-8 shadow-[0_4px_4px_0_rgba(0,0,0,0.25)]">
      <div className="flex gap-2 items-center">
        <div className="relative">
          <div className="w-[370px] flex items-center bg-[#F9FAFB] rounded-lg overflow-hidden border border-[#D1D5DB]">
            <div className="pl-4">
              <IoIosSearch className="text-[#6B7280] text-2xl" />
            </div>
            <input
              value={searchValue}
              onChange={onChangeHandler}
              type="text"
              placeholder="Enter an address, city or postal code"
              className="flex-1 py-4 px-2 text-sm text-black placeholder:text-[#bbb] placeholder:font-normal bg-[#F9FAFB] focus:outline-none"
            />
          </div>
          {searchResult && (
            <div
              ref={searchResultRef}
              className="w-[400px] h-auto overflow-y-auto max-h-[600px] bg-white absolute top-16 left-0 py-5 shadow-xl drop-shadow-2xl text-z-shark flex flex-col "
            >
              <>
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                <div
                  className="cursor-pointer flex items-center space-x-2 px-7"
                  onClick={handleCurrentLocationClick}
                  onKeyDown={handleCurrentLocationClick}
                >
                  <FaLocationArrow className="text-[#64A4F8] text-2xl" />
                  <span className="text-z-shark font-medium text-sm">
                    Use current location
                  </span>
                </div>
                <div
                  style={{
                    marginTop: filteredResults.city.length === 0 ? "0" : "1rem",
                  }}
                >
                  {filteredResults.city.length > 0 && (
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          height: "40px",
                          backgroundColor: "#F6F6F6",
                          padding: "0 1.5rem",
                        }}
                      >
                        <img
                          src={CityIcon}
                          alt="city icon"
                          style={{ marginRight: "0.5rem" }}
                        />
                        <span
                          style={{ fontSize: "0.875rem", fontWeight: "bold" }}
                        >
                          Cities
                        </span>
                      </div>
                      <div
                        style={{
                          borderTop: "1px solid #F1F1F1",
                          padding: "1rem 1.5rem",
                        }}
                      >
                        {filteredResults.city
                          .slice(
                            0,
                            isExpanded ? filteredResults.city.length : 5
                          )
                          .map((city, index) => (
                            // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
                            <div
                              key={index}
                              style={{
                                padding: "0.5rem 0",
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                handleCityClick(city.coordinates);
                              }}
                            >
                              <LuPlus style={{ marginRight: "0.5rem" }} />
                              {highlightMatch(city.city, searchValue)}
                            </div>
                          ))}
                        {filteredResults.city.length > 5 && (
                          <button
                            type="button"
                            className="text-[#7D7D7D] flex items-center gap-2 justify-center w-full h-10 font-bold text-sm"
                            onClick={() => setExpanded(!isExpanded)}
                          >
                            {isExpanded ? "Show Less" : "Show More"}
                            <img
                              src={GrayArrowDownIcon}
                              alt=""
                              className={`transition-transform duration-300 ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                            />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {!isLoading && properties && properties.length > 0 && (
                    <div className="bg-[#F6F6F6] text-[#32302F] px-6 font-bold text-sm py-2 flex items-center gap-3 mt-2">
                      <img src={ListingSearchIcon} alt="" />
                      Listings
                    </div>
                  )}
                  {isLoading ? (
                    <div className="text-center py-4">
                      <LoadingCircle />
                    </div>
                  ) : (
                    properties.map((property) => (
                      <div
                        key={property.listingId}
                        className="bg-white cursor-pointer w-full flex border-b border-b-[#E6E6E6] py-3"
                      >
                        <div
                          onClick={() => handleListingClick(property.listingId)}
                          onKeyDown={() =>
                            handleListingClick(property.listingId)
                          }
                        >
                          <img
                            src={property.displayImageUrl || ""}
                            alt={property.listingId}
                            className="w-full h-full object-cover  col-span-1 max-w-[110px] "
                          />
                        </div>
                        <div className="flex-1 flex flex-col px-3 py-4 justify-between">
                          {property.listPrice && (
                            <div className="w-full text-xs text-black flex items-center relative">
                              <div
                                className="flex-1 text-2xl text-black font-bold flex items-center"
                                onClick={() =>
                                  handleListingClick(property.listingId)
                                }
                                onKeyDown={() =>
                                  handleListingClick(property.listingId)
                                }
                              >
                                {`$${formatPrice(property.listPrice / 100)}`}
                              </div>
                            </div>
                          )}

                          <div
                            onClick={() =>
                              handleListingClick(property.listingId)
                            }
                            onKeyDown={() =>
                              handleListingClick(property.listingId)
                            }
                            className="flex items-center text-z-dark-moss-green text-xs font-bold w-full justify-between gap-1"
                          >
                            <div className="w-5/10 whitespace-nowrap tracking-tight">
                              Down Payment Contribution
                            </div>
                            <div className="w-px h-3 bg-gray-400 " />
                            <div className="w-5/10 pl-1 flex justify-between">
                              {property.downPaymentBoost
                                ? `$${formatPrice(
                                    convertToFormattedAmount(
                                      property.downPaymentBoost
                                    )
                                  )}`
                                : "N/A"}
                            </div>
                          </div>
                          <div
                            onClick={() =>
                              handleListingClick(property.listingId)
                            }
                            onKeyDown={() =>
                              handleListingClick(property.listingId)
                            }
                            className="text-sm text-black font-light whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px]"
                          >
                            {property.displayAddress}
                          </div>

                          <div
                            onClick={() =>
                              handleListingClick(property.listingId)
                            }
                            onKeyDown={() =>
                              handleListingClick(property.listingId)
                            }
                            className="w-full text-xs text-black pt-2 flex items-center"
                          >
                            <div className="flex-1 flex items-center">
                              <div className="text-sm flex items-center gap-3">
                                <PropertyDetails
                                  icon={bedIcon}
                                  label="Beds"
                                  value={
                                    property.numBedrooms
                                      ? `${property.numBedrooms}`
                                      : `${"N/A"}`
                                  }
                                />
                                <PropertyDetails
                                  icon={bathIcon}
                                  label="Baths"
                                  value={
                                    property.numBathrooms
                                      ? `${property.numBathrooms}`
                                      : `${"N/A"}`
                                  }
                                />
                                <PropertyDetails
                                  icon={SqftIcon}
                                  label="Sqft"
                                  value={
                                    property.sqft
                                      ? `${formatPrice(property.sqft)}`
                                      : "N/A"
                                  }
                                />
                              </div>
                            </div>
                            <div className="flex-2 text-right  flex items-center justify-end">
                              <span className="inline-block w-2.5 h-2.5 bg-[#6ECE35] rounded-full mr-1 tracking-tight" />
                              {property.listDate && (
                                <span className="inline-block tracking-tight">
                                  {formatDate(property.listDate)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            </div>
          )}
        </div>
        <div className="rounded-xl text-sm cursor-pointer font-semibold border border-[#E7E7E7] h-[60px] flex items-center justify-center">
          <DropdownMultiple
            positionY="top"
            label="Property Type"
            options={propertyTypeLabels}
          />
        </div>
        <div ref={bedAndBathBoxRef} className="relative">
          <div
            className="rounded-xl text-sm cursor-pointer font-semibold border border-[#E7E7E7] h-[60px] flex items-center justify-center px-5"
            onClick={() => setOpenBedAndBath((prev) => !prev)}
            onKeyDown={() => setOpenBedAndBath((prev) => !prev)}
          >
            <span>Beds & Baths</span>
            <BiChevronDown
              className={clsx(
                "text-xl transition-all",
                openBedAndBath && "rotate-180"
              )}
            />
          </div>
          {openBedAndBath && (
            <div className="absolute z-10 top-16 p-4 divide-y divide-[#EBEBEB] border-gray-100 border w-[360px] h-auto bg-white rounded-lg shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.10),0px_2px_4px_-2px_rgba(0,0,0,0.05)]">
              <MultiSelectGroup
                customMultiSelect={true}
                textClassName="!font-normal"
                gap="12px"
                selectedValue={beds}
                label="Bedrooms"
                onChange={(values) => {
                  const updatedValues = values.map((value) =>
                    value === "5+" ? "5" : value
                  );
                  setBeds(updatedValues);
                }}
                options={propertyPreferences.beds.map((item) => ({
                  label: item.value === 5 ? "5+" : item.label,
                  value: String(item.value === null ? "All" : item.value),
                }))}
                customClassName="text-[#B8B8B8]"
                spaceX="pb-5 !mb-0 !space-y-1.5"
                labelTextSize="!text-[16px]"
              />
              <MultiSelectGroup
                customMultiSelect={true}
                textClassName="!font-normal"
                gap="12px"
                selectedValue={baths}
                label="Bathrooms"
                onChange={(values) => {
                  const updatedValues = values.map((value) =>
                    value === "5+" ? "5" : value
                  );
                  setBaths(updatedValues);
                }}
                options={propertyPreferences.beds.map((item) => ({
                  label: item.value === 5 ? "5+" : item.label,
                  value: String(item.value === null ? "All" : item.value),
                }))}
                customClassName="text-[#B8B8B8]"
                spaceX="pt-5 !mb-0 !space-y-2"
                labelTextSize="!text-[16px]"
              />
            </div>
          )}
        </div>
        <div ref={priceBoxRef} className="relative">
          <div
            className="space-x-1 rounded-xl text-sm cursor-pointer font-semibold border border-[#E7E7E7] h-[60px] flex items-center justify-center px-5"
            onClick={() => setOpenPriceBox((prev) => !prev)}
            onKeyDown={() => setOpenPriceBox((prev) => !prev)}
          >
            <span>${formatPrice(propertyPrice[0])}</span>
            <span>-</span>
            <span>
              {propertyPrice[1] === MAX_PRICE
                ? "Max"
                : `$${formatPrice(propertyPrice[1])}`}
            </span>
            <BiChevronDown
              className={clsx(
                "text-xl transition-all",
                openPriceBox && "rotate-180"
              )}
            />
          </div>
          {openPriceBox && (
            <div className="absolute z-20 top-16 p-5 divide-y divide-[#EBEBEB] border-gray-100 border w-[360px] bg-white rounded-lg shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.10),0px_2px_4px_-2px_rgba(0,0,0,0.05)]">
              <div className="space-y-8 relative">
                <div className="flex items-center justify-between">
                  <div className="text-z-shark font-medium">Price Range</div>
                  <div
                    className="text-[#D0D0D0] font-normal border border-[#D0D0D0] py-1 px-2 rounded-lg cursor-pointer"
                    onClick={() => {
                      setPropertyPrice([0, 5000000]);
                    }}
                    onKeyDown={() => {
                      setPropertyPrice([0, 5000000]);
                    }}
                  >
                    Reset
                  </div>
                </div>

                <Range
                  values={propertyPrice}
                  step={25000}
                  min={MIN}
                  max={MAX_PRICE}
                  onChange={(values) => {
                    const [minValue, maxValue] = values;
                    if (maxValue < minValue) {
                      setPropertyPrice([minValue, minValue]);
                    } else {
                      setPropertyPrice([
                        Math.min(minValue, MAX_PRICE),
                        Math.min(maxValue, MAX_PRICE),
                      ]); // Clamp to MAX_PRICE
                    }
                  }}
                  renderTrack={({ props, children }) => (
                    <div
                      {...props}
                      className="w-full h-2 bg-[rgba(0,0,0,0.04)] rounded"
                      style={{
                        ...props.style,
                        background: getTrackBackground({
                          values: propertyPrice,
                          colors: [
                            "rgba(0, 0, 0, 0.04)",
                            "#456930",
                            "rgba(0, 0, 0, 0.04)",
                          ],
                          min: MIN,
                          max: MAX_PRICE,
                        }),
                      }}
                    >
                      {children}
                    </div>
                  )}
                  renderThumb={({ props }) => (
                    <div
                      {...props}
                      className="h-6 w-6 border-2 border-z-dark-moss-green bg-white rounded-full flex items-center justify-center focus:outline-none relative"
                      style={{
                        ...props.style,
                        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.10)",
                      }}
                    />
                  )}
                />

                <div className="flex justify-between w-full">
                  <div className="flex flex-col w-[150px]">
                    <div className="text-xs mb-2">Minimum Price</div>
                    <input
                      type="text"
                      value={`$${formatPrice(propertyPrice[0])}`}
                      onChange={(e) => {
                        const minValue =
                          parseInt(e.target.value.replace(/\D/g, ""), 10) || 0;
                        let maxValue = propertyPrice[1];
                        if (minValue > maxValue) {
                          maxValue = minValue;
                        }
                        setPropertyPrice([
                          Math.min(minValue, MAX_PRICE),
                          Math.min(maxValue, MAX_PRICE),
                        ]);
                      }}
                      className="border border-[#F3F3F3] h-[45px] rounded font-normal text-z-shark p-3 flex items-center"
                    />
                  </div>
                  <div className="flex flex-col w-[150px]">
                    <div className="text-xs mb-2">Maximum Price</div>
                    <input
                      type="text"
                      value={
                        propertyPrice[1] === MAX_PRICE
                          ? "Max"
                          : `$${formatPrice(propertyPrice[1])}`
                      }
                      onChange={(e) => {
                        const maxValue =
                          parseInt(e.target.value.replace(/\D/g, ""), 10) || 0;
                        let minValue = propertyPrice[0];
                        if (maxValue < minValue) {
                          minValue = maxValue;
                        }
                        setPropertyPrice([
                          Math.min(minValue, MAX_PRICE),
                          Math.min(maxValue, MAX_PRICE),
                        ]);
                      }}
                      className="border border-[#F3F3F3] h-[45px] rounded font-normal text-z-shark p-3 flex items-center"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div
          className={clsx(
            "rounded-xl text-sm cursor-pointer font-semibold border h-[60px] flex items-center justify-center",
            togglesPropertyType.active
              ? "border-z-dark-moss-green"
              : "border-[#E7E7E7]"
          )}
        >
          <span
            onClick={() => togglePropertyTypeButton("active")}
            onKeyDown={() => togglePropertyTypeButton("active")}
            className={clsx(
              "px-4 h-full flex items-center justify-center",
              togglesPropertyType.active && "text-z-dark-moss-green"
            )}
          >
            Active
          </span>
          {togglesPropertyType.active && (
            <div className="w-full z-10 h-full rounded-r-lg flex items-center justify-center px-4 bg-z-dark-moss-green">
              <Dropdown
                selectedOptions={selectedOptions}
                setSelectedOption={setSelectedOption}
                top="top-14"
                positionY="top"
                label="active"
                customClassName="h-full flex item-center text-white"
                options={propertyActiveFilter}
              />
            </div>
          )}
        </div>
        <div
          className={clsx(
            "rounded-xl text-sm cursor-pointer font-semibold border h-[60px] flex items-center justify-center",
            togglesPropertyType.sold ? "border-[#C10230]" : "border-[#E7E7E7]"
          )}
        >
          <span
            onClick={() => {
              if (authenticated) {
                togglePropertyTypeButton("sold");
              } else {
                setShowLoginModal(true);
              }
            }}
            onKeyDown={() => {
              if (authenticated) {
                togglePropertyTypeButton("sold");
              } else {
                setShowLoginModal(true);
              }
            }}
            className={clsx(
              "px-4 h-full flex items-center justify-center",
              togglesPropertyType.sold && "text-[#C10230]"
            )}
          >
            Sold
          </span>
          {togglesPropertyType.sold && (
            <div className="w-full z-10 h-full rounded-r-lg flex items-center justify-center px-4 bg-[#C10230]">
              <Dropdown
                selectedOptions={selectedOptions}
                setSelectedOption={setSelectedOption}
                top="top-14"
                positionY="top"
                label="sold"
                customClassName="h-full flex item-center text-white"
                options={propertyDeListedAndSoldFilter}
              />
            </div>
          )}
        </div>
        <div
          onClick={() => setOpenFilterPanel(!openFilterPanel)}
          onKeyPress={() => setOpenFilterPanel(true)}
          className="rounded-xl text-sm font-semibold cursor-pointer flex items-center gap-2 border text-white bg-z-dark-moss-green border-[#E7E7E7] h-[60px] justify-center px-5"
        >
          <VscSettings className="text-2xl" />
          More
        </div>
      </div>
      <div
        ref={filterBoxRef}
        className={`absolute top-0 pt-8 overflow-hidden pb-14 inset-x-0 bg-white w-full h-[700px] max-h-[700px] shadow-[0_6px_11.1px_0_rgba(0,0,0,0.25)] z-[60]
					transform transition-all duration-300 ease-in-out ${
            openFilterPanel
              ? "-translate-y-0 opacity-100 visible"
              : "-translate-y-[120vh] opacity-0 invisible"
          }`}
      >
        <div className="container mx-auto">
          <div className="flex flex-col w-full">
            <div className="flex items-center justify-between">
              <span className="text-4xl text-z-shark font-semibold">
                Filters
              </span>
              <IoCloseCircleOutline
                className="text-3xl cursor-pointer"
                onClick={() => setOpenFilterPanel(false)}
              />
            </div>
            <div className="mt-3">
              <FilterPanel panelOpen={filterPanelVisible} />
            </div>
          </div>
        </div>
      </div>
      <div className="mr-11 flex items-center gap-2">
        <div className="flex items-center overflow-hidden rounded-2xl border border-[#CECECE]">
          <div
            className={clsx(
              "w-[60px] h-[60px] rounded-[15px] cursor-pointer flex items-center justify-center",
              switchMode === "list"
                ? "bg-z-dark-moss-green text-white"
                : "text-z-dark-moss-green"
            )}
            onClick={() => setSwitchMode("list")}
            onKeyDown={() => setSwitchMode("list")}
          >
            <IoIosList className="text-3xl" />
          </div>
          <div
            className={clsx(
              "w-[60px] h-[60px] rounded-[15px] cursor-pointer flex items-center justify-center",
              switchMode === "map"
                ? "bg-z-dark-moss-green text-white"
                : "text-z-dark-moss-green"
            )}
            onClick={() => setSwitchMode("map")}
            onKeyDown={() => setSwitchMode("map")}
          >
            <PiMapTrifold className="text-3xl" />
          </div>
        </div>
      </div>
    </div>
  );
};

export { SearchField };
